<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Performance dos Agentes IA',
  },
});

const byAgent = computed(() => props.data?.by_agent || []);
const tokenUsage = computed(() => props.data?.token_usage || {});

function formatNumber(num) {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('pt-BR');
}

function getSuccessColor(rate) {
  if (rate >= 95) return 'text-n-teal-11';
  if (rate >= 90) return 'text-n-blue-11';
  if (rate >= 80) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-sparkles text-lg text-n-pink-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <span class="text-xs text-n-slate-11">
        {{ data.active_agents || 0 }}/{{ data.total_agents || 0 }} ativos
      </span>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Summary Stats -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-xl font-bold text-n-slate-12">
            {{ formatNumber(data.total_executions) }}
          </div>
          <div class="text-xs text-n-slate-11">Execucoes</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div
            class="text-xl font-bold"
            :class="getSuccessColor(data.overall_success_rate)"
          >
            {{ data.overall_success_rate || 0 }}%
          </div>
          <div class="text-xs text-n-slate-11">Taxa Sucesso</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-xl font-bold text-n-blue-11">
            {{ formatNumber(data.total_tokens) }}
          </div>
          <div class="text-xs text-n-slate-11">Tokens Usados</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-xl font-bold text-n-ruby-11">
            {{ data.total_errors || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Erros</div>
        </div>
      </div>

      <!-- By Agent -->
      <div v-if="byAgent.length > 0" class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">Por Agente IA</span>
        <div class="flex flex-col gap-2 max-h-64 overflow-y-auto">
          <div
            v-for="agent in byAgent"
            :key="agent.agent_id"
            class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-1"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span
                  class="w-2 h-2 rounded-full"
                  :class="
                    agent.status === 'active' ? 'bg-n-teal-9' : 'bg-n-slate-7'
                  "
                />
                <span class="text-sm font-medium text-n-slate-12">{{
                  agent.agent_name
                }}</span>
                <span class="text-xs text-n-slate-10">{{
                  agent.agent_type
                }}</span>
              </div>
              <span class="text-sm font-bold text-n-slate-12">{{
                agent.total_executions
              }}</span>
            </div>
            <div class="grid grid-cols-4 gap-2 text-xs">
              <div>
                <span class="text-n-slate-10">Sucesso:</span>
                <span
                  :class="getSuccessColor(agent.success_rate)"
                  class="font-medium ml-1"
                >
                  {{ agent.success_rate }}%
                </span>
              </div>
              <div>
                <span class="text-n-slate-10">Tokens:</span>
                <span class="text-n-slate-12 font-medium ml-1">{{
                  formatNumber(agent.tokens_used)
                }}</span>
              </div>
              <div>
                <span class="text-n-slate-10">Tempo:</span>
                <span class="text-n-slate-12 font-medium ml-1">{{ agent.avg_execution_time_ms }}ms</span>
              </div>
              <div>
                <span class="text-n-slate-10">Erros:</span>
                <span class="text-n-ruby-11 font-medium ml-1">{{
                  agent.error_count
                }}</span>
              </div>
            </div>
            <div v-if="agent.inboxes?.length > 0" class="flex flex-wrap gap-1">
              <span
                v-for="inbox in agent.inboxes.slice(0, 3)"
                :key="inbox"
                class="px-2 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
              >
                {{ inbox }}
              </span>
              <span
                v-if="agent.inboxes.length > 3"
                class="text-xs text-n-slate-10"
              >
                +{{ agent.inboxes.length - 3 }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Token Usage by Model -->
      <div
        v-if="Object.keys(tokenUsage.by_model || {}).length > 0"
        class="flex flex-col gap-2 pt-2 border-t border-n-weak"
      >
        <span class="text-sm font-medium text-n-slate-12">Tokens por Modelo</span>
        <div class="flex flex-wrap gap-2">
          <div
            v-for="(tokens, model) in tokenUsage.by_model"
            :key="model"
            class="px-3 py-1.5 rounded-lg bg-n-alpha-1"
          >
            <span class="text-xs text-n-slate-11">{{ model }}:</span>
            <span class="text-sm font-medium text-n-slate-12 ml-1">{{
              formatNumber(tokens)
            }}</span>
          </div>
        </div>
      </div>

      <!-- Performance Stats -->
      <div
        class="flex items-center justify-between text-xs text-n-slate-11 pt-2 border-t border-n-weak"
      >
        <span>Media: {{ data.avg_execution_time_ms || 0 }}ms/execucao</span>
        <span>{{ tokenUsage.avg_per_execution || 0 }} tokens/execucao</span>
      </div>
    </div>
  </div>
</template>
